import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
    Lock,
    AlertCircle,
    CheckCircle,
    Eye,
    EyeOff
} from 'lucide-react';
import './Login.css';
import logo from '../assets/logo.png';
import { authApi } from '../services/authApi';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token. Please request a new password reset link.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 12) {
            setError('Password must be at least 12 characters long');
            return;
        }

        if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
            setError('Password must include uppercase, lowercase, number, and special character');
            return;
        }

        setLoading(true);

        try {
            // Use backend API for password reset
            try {
                await authApi.resetPassword(token, password);
                setSuccess(true);

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } catch (backendError) {
                const errorMsg = typeof backendError === 'string' ? backendError : backendError?.message || 'Failed to reset password';
                throw new Error(errorMsg);
            }
        } catch (err) {
            console.error('Reset password error:', err);
            setError(err?.message || 'Unable to reset password. Please try again or request a new reset link.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="login-page">
                <div className="login-page__frame">
                    <header className="login-brand">
                        <div className="login-brand__inner">
                            <span className="login-brand__tab" aria-hidden="true" />
                            <div className="login-brand__logo-wrap">
                                <img src={logo} alt="LinkLoad Logo" className="login-brand__logo" />
                            </div>
                            <div className="login-brand__text">
                                <h1 className="login-brand__title">Create New Password</h1>
                                <p className="login-brand__subtitle">
                                    Choose a strong password for your account.
                                </p>
                            </div>
                            <span className="login-brand__chip" aria-hidden="true" />
                        </div>
                    </header>
                    <section className="login-card">
                        <div className="login-card__surface">
                            {!success ? (
                                <>
                                    <div className="login-card__header">
                                        <div className="login-card__badge">
                                            <Lock size={30} color="#000" />
                                        </div>
                                        <h1 className="login-card__title">Reset Password</h1>
                                        <p className="login-card__subtitle">
                                            Enter your new password below.
                                        </p>
                                    </div>

                                    {error && (
                                        <div className="login-alert" role="alert">
                                            <AlertCircle size={18} />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    <form className="login-form" onSubmit={handleSubmit}>
                                        <div className="login-input-group">
                                            <label className="login-input-label" htmlFor="new-password">
                                                <Lock size={16} />
                                                New Password
                                            </label>
                                            <div className="login-input-wrapper">
                                                <input
                                                    id="new-password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    className="login-input"
                                                    placeholder="••••••••"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    autoComplete="new-password"
                                                    required
                                                    disabled={!token}
                                                />
                                                <button
                                                    type="button"
                                                    className="login-input-toggle"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="login-input-group">
                                            <label className="login-input-label" htmlFor="confirm-password">
                                                <Lock size={16} />
                                                Confirm Password
                                            </label>
                                            <div className="login-input-wrapper">
                                                <input
                                                    id="confirm-password"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    className="login-input"
                                                    placeholder="••••••••"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    autoComplete="new-password"
                                                    required
                                                    disabled={!token}
                                                />
                                                <button
                                                    type="button"
                                                    className="login-input-toggle"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    tabIndex={-1}
                                                >
                                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            className="login-submit"
                                            disabled={loading || !token}
                                        >
                                            {loading ? 'Resetting...' : 'Reset Password'}
                                        </button>
                                    </form>

                                    <div className="login-meta">
                                        <Link className="login-meta__link" to="/login">
                                            Back to login
                                        </Link>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="login-card__header">
                                        <div className="login-card__badge login-card__badge--success">
                                            <CheckCircle size={30} color="#000" />
                                        </div>
                                        <h1 className="login-card__title">Password Reset Successful</h1>
                                        <p className="login-card__subtitle">
                                            Your password has been successfully reset. You can now log in with your new password.
                                        </p>
                                    </div>

                                    <div className="login-note" style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
                                        Redirecting to login page...
                                    </div>

                                    <div className="login-meta" style={{ marginTop: 'var(--spacing-4)' }}>
                                        <Link className="login-meta__link" to="/login">
                                            Go to login
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </div >
        </Layout >
    );
};

export default ResetPassword;
